/**
 * EmbedView — strict read-only post view designed for inline-iframe usage.
 *
 * Auth model (CloudV2 Draft-Support):
 *  1. On mount the iframe sends `{type: 'embed-ready'}` to window.parent.
 *  2. Parent (a trusted portal origin, see VITE_EMBED_TRUSTED_ORIGINS) replies
 *     with `{type: 'embed-auth', token: <jwt>}` if the user is logged in.
 *  3. We hold the token in component state only — NOT localStorage — and
 *     pass it as Authorization: Bearer on every API call from inside the
 *     iframe. Origin of incoming postMessage is whitelisted.
 *  4. If no token arrives within 300ms the embed simply fetches anonymously,
 *     which keeps published-post embeds in public chats working.
 *
 * Visual:
 *  - cover_image_url / first unreferenced media → hero
 *  - remaining unreferenced media → 60x60 thumbnail strip
 *  - body via parseMarkdown (Mermaid, code, etc.)
 *  - all body links forced to target=_blank so iframe stays put
 *
 * Einbettung in fremde Seiten (08.10.2026, Drone-Challenge-Homepage):
 *  - `?chrome=0` blendet die Kopfzeile aus.
 *  - `?lang=de` zeigt die Variante der Sprachgruppe in dieser Sprache,
 *    falls es sie gibt; sonst den aufgerufenen Beitrag.
 *  - `?toc=1` setzt ein Inhaltsverzeichnis vor den Text, wenn das
 *    Markdown kein `{{TOC}}` hat.
 *  - `#<anker>` scrollt zur Überschrift mit dieser id.
 *  - Nach dem Rendern und bei jeder Größenänderung geht
 *    `{type: 'embed-height', postId, height}` an die Elternseite, damit
 *    sie den iFrame ohne inneren Scrollbalken hoch genug macht.
 */

import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { parseMarkdown } from '../utils/markdownConfig'
import { useMermaidAsync } from '../hooks/useMermaidAsync'
import { Presentation3DViewer } from '../components/Presentation3DViewer'
import { CONTENT_API, PRESENTER_3D_ENABLED } from '../config'
import type { MediaItem, Post, PostLanguagesResponse } from '../types'
import '../styles/global.css'

// Origins that are trusted to provide auth tokens for embedded posts.
// Tenant-safe (#1376): comes from VITE_EMBED_TRUSTED_ORIGINS (comma-
// separated); the operator list lives in .env.production. Default =
// same-origin only, so a tenant build never trusts an operator portal.
const TRUSTED_PARENT_ORIGINS: string[] = (
  (import.meta.env.VITE_EMBED_TRUSTED_ORIGINS as string | undefined) || ''
)
  .split(',')
  .map((s) => s.trim().replace(/\/$/, ''))
  .filter(Boolean)
  .concat(typeof window !== 'undefined' ? [window.location.origin] : [])

const API_BASE = CONTENT_API + '/api/v1'

interface EmbedAuthMessage {
  type: 'embed-auth'
  token: string | null
}

function isEmbedAuth(data: unknown): data is EmbedAuthMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'embed-auth'
  )
}

async function fetchJson<T>(path: string, token: string | null): Promise<T> {
  // Strict tenant isolation can make a published post invisible to a
  // logged-in caller from a different tenant (or to a tenant-member
  // looking at a partner_id=NULL legacy post). For an embed that's
  // useless: the post may be public to anonymous viewers but invisible
  // to the authed iframe. So any failure of the AUTHED attempt
  // (401/403/404/5xx) falls through to the anonymous fetch, which only
  // sees what a public visitor of /share/:id sees anyway (CloudV2
  // review 2026-08-18).
  if (token) {
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    })
    if (res.ok) return res.json() as Promise<T>
  }
  const res = await fetch(`${API_BASE}${path}`, { headers: { 'Content-Type': 'application/json' } })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

/** Beitrags-ID der gewünschten Sprache aus der Sprachgruppe, sonst die aufgerufene. */
async function resolveLanguage(id: string, lang: string | null, token: string | null): Promise<string> {
  if (!lang) return id
  try {
    const langs = await fetchJson<PostLanguagesResponse>(`/posts/${id}/languages`, token)
    const hit = langs.variants.find((v) => v.language === lang)
    return hit ? String(hit.post_id) : id
  } catch {
    return id
  }
}

async function fetchPost(id: string, token: string | null, lang: string | null): Promise<Post> {
  const target = await resolveLanguage(id, lang, token)
  return fetchJson<Post>(`/posts/${target}`, token)
}

/** Markdown um ein Inhaltsverzeichnis ergänzen, wenn keines gesetzt ist. */
function withToc(markdown: string): string {
  return /^\s*\{\{TOC\}\}\s*$/m.test(markdown) ? markdown : `{{TOC}}\n\n${markdown}`
}

export function EmbedView() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const lang = params.get('lang')
  const showChrome = params.get('chrome') !== '0'
  const wantToc = params.get('toc') === '1'
  const contentRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  // Diagrammarten, die beautiful-mermaid nicht kennt (gantt, pie, …), zeichnet
  // das klassische mermaid nach dem Einhängen, wie im Editor (Post #5447).
  const [mermaidRoot, setMermaidRoot] = useState<HTMLDivElement | null>(null)
  useMermaidAsync(mermaidRoot)

  // Token from postMessage handshake. null until first message (or fallback).
  const [embedToken, setEmbedToken] = useState<string | null>(null)
  const [handshakeDone, setHandshakeDone] = useState(false)

  // ── postMessage handshake ─────────────────────────────────────
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null

    function onMessage(event: MessageEvent) {
      if (!TRUSTED_PARENT_ORIGINS.includes(event.origin)) return
      if (!isEmbedAuth(event.data)) return
      setEmbedToken(event.data.token ?? null)
      setHandshakeDone(true)
      if (timer) { clearTimeout(timer); timer = null }
    }

    window.addEventListener('message', onMessage)

    // Tell the parent we are ready to receive auth.
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage({ type: 'embed-ready' }, '*')
      } catch { /* parent on different origin and refused — ignore */ }
    } else {
      // We are NOT in an iframe → no handshake possible, proceed anon.
      setHandshakeDone(true)
    }

    // Fall back to anonymous after 300ms if no parent response.
    timer = setTimeout(() => setHandshakeDone(true), 300)

    return () => {
      window.removeEventListener('message', onMessage)
      if (timer) clearTimeout(timer)
    }
  }, [])

  // ── Fetch post once handshake settled ─────────────────────────
  const { data: post, isLoading, error } = useQuery({
    queryKey: ['embed-post', id, lang, embedToken],
    queryFn: () => fetchPost(id!, embedToken, lang),
    enabled: !!id && handshakeDone,
  })

  // Sort media: hero role first, then by position. Drop items that are
  // already referenced inline in the markdown to avoid double rendering.
  const unreferencedMedia = useMemo<MediaItem[]>(() => {
    if (!post?.media || post.media.length === 0) return []
    const sorted = [...post.media].sort((a, b) => {
      if (a.role === 'hero' && b.role !== 'hero') return -1
      if (b.role === 'hero' && a.role !== 'hero') return 1
      return (a.position ?? 0) - (b.position ?? 0)
    })
    const content = post.content || ''
    return sorted.filter(m => {
      if (m.media_url && content.includes(m.media_url)) return false
      if (m.storage_id && content.includes(`/media/${m.storage_id}`)) return false
      return true
    })
  }, [post?.media, post?.content])

  const heroSrc = post?.cover_image_url || unreferencedMedia[0]?.media_url || null
  const stripItems = post?.cover_image_url ? unreferencedMedia : unreferencedMedia.slice(1)

  // Force all body links into a new tab so clicks don't break the iframe.
  useEffect(() => {
    if (!contentRef.current) return
    contentRef.current.querySelectorAll('a').forEach(a => {
      if (!a.target || a.target === '_self') {
        a.target = '_blank'
        a.rel = 'noopener noreferrer'
      }
    })
  }, [post])

  // Höhe an die Elternseite melden: nach dem Rendern und bei jeder
  // Größenänderung des Inhalts (Bilder, Diagramme, die später laden).
  useEffect(() => {
    const body = bodyRef.current
    if (!body || !post || window.parent === window) return
    const report = () => {
      const header = headerRef.current?.offsetHeight ?? 0
      const inner = contentRef.current?.scrollHeight ?? body.scrollHeight
      try {
        window.parent.postMessage({ type: 'embed-height', postId: post.id, height: header + inner }, '*')
      } catch { /* Elternseite nicht erreichbar */ }
    }
    report()
    const observer = new ResizeObserver(report)
    observer.observe(body)
    return () => observer.disconnect()
  }, [post])

  // Anker aus der URL: zur Überschrift mit dieser id scrollen.
  useEffect(() => {
    if (!post || !window.location.hash) return
    const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)))
    target?.scrollIntoView({ block: 'start' })
  }, [post])

  if (!handshakeDone || isLoading) {
    return <div className="embed-state">Lade…</div>
  }
  if (error || !post) {
    return <div className="embed-state embed-state--error">Beitrag nicht gefunden.</div>
  }

  const html = parseMarkdown(wantToc ? withToc(post.content || '') : post.content || '')

  // Special doc-type: 3D presentation → render the 3DPresenter iframe
  // full-bleed inside the embed instead of the markdown source.
  if (post.doc_type === '3dpresentation' || post.doc_type === '3d_presentation') {
    return (
      <div className="embed-wrap">
        {PRESENTER_3D_ENABLED && <Presentation3DViewer post={post} compact />}
      </div>
    )
  }

  return (
    <div className="embed-wrap">
      {showChrome && (
      <div className="embed-header" ref={headerRef}>
        <span className="embed-title" title={post.title}>{post.title}</span>
        <a
          className="embed-open"
          href={`/post/${post.id}`}
          target="_top"
          title="Im Content-Portal öffnen"
        >
          ↗
        </a>
      </div>
      )}
      <div
        ref={contentRef}
        className="embed-content editor-content"
      >
        <div ref={bodyRef}>
        {heroSrc && (
          <img className="embed-hero" src={heroSrc} alt="" loading="lazy" />
        )}
        {stripItems.length > 0 && (
          <div className="embed-strip">
            {stripItems.slice(0, 6).map(m => (
              <img
                key={m.id ?? m.storage_id}
                src={m.media_url}
                alt={m.caption || ''}
                loading="lazy"
              />
            ))}
          </div>
        )}
        <div ref={setMermaidRoot} dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      </div>
    </div>
  )
}
